/**
 * generationEstimate.ts — WEB-830: оценка стоимости генерации воронки.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ. Это денежный путь: показанное число обязано быть
 * ПОТОЛКОМ — фактическое списание не может его превысить (ТЗ PM 2026-08-01,
 * п.3; та же претензия с другой стороны, что и в WEB-904). Формула зеркалит
 * бэкенд, поэтому живёт в одном месте и покрыта тестами с граничными числами.
 *
 * ЧТО СПИСЫВАЕТСЯ НА САМОМ ДЕЛЕ — две независимые проводки:
 *
 *  1. Текстовая модель. `funnel-generation.service.ts` на постановке в очередь
 *     считает потолок: effectivePro = mode === 'pro' || !!styleImageKey, далее
 *     каталожная цена pro_generation (opus) либо simple_generation (sonnet).
 *     Процессор списывает Math.min(стоимость по токенам, этот потолок), то
 *     есть каталожное число и есть верхняя граница этой части.
 *
 *  2. Картинки. Отдельная проводка (`funnel-generation.processor.ts`,
 *     operationType IMAGE) с allowOvershoot — потолком текстовой модели она НЕ
 *     ограничена. Число картинок ограничено только капом из
 *     `image-generation.service.ts`: Math.max(IMAGE_CAP_PER_FUNNEL,
 *     screenCount), где screenCount — ВСЕ экраны воронки (квиз + пейволл).
 *     Именно эта часть и растёт с числом экранов (WEB-873), поэтому в оценку
 *     она обязана входить явно — иначе показанное окажется меньше списанного.
 *
 * В простом режиме картинки не генерируются, поэтому там оценка от числа
 * экранов не зависит: это честный потолок, а не забытое слагаемое.
 *
 * WEB-1242 (хвост, 24.08) — ГЛАВНОЕ ПРАВИЛО ЭТОГО МОДУЛЯ. Считать надо не от
 * того, что видно в полях формы, а от того, что РЕАЛЬНО уедет в запрос
 * (requestScreenCounts ниже — ею же форма строит payload). Не присланное поле
 * сервер разрешает по-своему: для картинок это верхняя граница вида генерации
 * (cost-catalog-retail.ts::totalScreensForImageEstimate), для текста —
 * каталожная полка (funnel-generation.service.ts::totalScreensForEstimate).
 * Обе ветки зеркалятся здесь и сверяются структурно настоящим прогоном обеих
 * сторон: backend/src/ai-funnels/cost/generation-estimate-contract.spec.ts.
 */

/**
 * Нижний порог капа картинок. Зеркало
 * `ImageGenerationService.IMAGE_CAP_PER_FUNNEL` (backend). Значение приходит с
 * бэкенда ключом `image_cap_floor` каталога цен; эта константа — запасной
 * вариант на случай старого ответа, чтобы оценка не обнулила картинки.
 */
export const DEFAULT_IMAGE_CAP_FLOOR = 12;

/** Ключ каталога, которым бэкенд отдаёт актуальный порог капа картинок. */
export const IMAGE_CAP_FLOOR_KEY = 'image_cap_floor';

/**
 * WEB-830 QA-hold fix (06.08) — зеркало backend/.../ai-funnels.dto.ts
 * QUIZ_SCREEN_COUNT_MAX/PAYWALL_SCREEN_COUNT_MAX. Запасной вариант на случай
 * старого ответа каталога без этих полей — как и DEFAULT_IMAGE_CAP_FLOOR.
 */
// WEB-1371 (догон, зеркала серверных чисел — cost-catalog-retail.ts):
// экраны оплаты полной воронки по умолчанию (claude-funnel-client `?? 2`).
const DEFAULT_PAYWALL_SCREEN_COUNT = 2;
// Пол потолка простой генерации = её себестоимость (costFor = 40 raw-центов).
const SIMPLE_GENERATION_COST_FLOOR_CREDITS = 40;

export const DEFAULT_QUIZ_SCREEN_COUNT_MAX = 40;
export const DEFAULT_PAYWALL_SCREEN_COUNT_MAX = 15;

/**
 * WEB-2389 заход 1 (решение владельца 08.10) — зеркало серверного
 * `SIMPLE_GENERATION_SCREEN_REFERENCE` (cost-catalog-retail.ts): эталон, к
 * которому пропорцией приводится потолок цены простой генерации. Заморожен
 * числом на обеих сторонах: раньше здесь он выводился из максимумов экранов
 * каталога (quiz_screen_count_max + paywall_screen_count_max), и подъём
 * потолка заказа квиза до 150 молча удешевил бы экран втрое. Паритет с
 * сервером держит backend/src/ai-funnels/cost/generation-estimate-contract.spec.ts.
 */
export const SIMPLE_GENERATION_SCREEN_REFERENCE = 55;

/** Ключи каталога для денежных максимумов экранов (см. cost-catalog-retail.ts). */
export const QUIZ_SCREEN_COUNT_MAX_KEY = 'quiz_screen_count_max';
export const PAYWALL_SCREEN_COUNT_MAX_KEY = 'paywall_screen_count_max';

export type EstimateMode = 'simple' | 'pro';
export type EstimateComposition = 'funnel' | 'quiz' | 'paywall';

export interface GenerationEstimateParams {
  /** Каталог розничных цен (Redux `billing.costCatalog`). */
  catalog: Record<string, number> | null | undefined;
  mode: EstimateMode;
  composition: EstimateComposition;
  /** Экраны квиза — либо единственное поле экранов для квиз/пейволл-онли. */
  screenCount: number;
  /** Экраны пейволла — только для полной воронки. */
  paywallScreenCount?: number;
  /** Загруженная стилевая картинка: сама по себе переводит генерацию в Pro. */
  styleImageKey?: string;
  /**
   * WEB-830 QA-hold fix (06.08): true (default, включая случаи, когда поле
   * вовсе не передано — обратная совместимость с прежними вызывающими и с
   * простыми юнит-тестами) означает «screenCount — осознанный выбор», и
   * простой режим масштабирует по нему оценку. false — счётчик не тронут
   * (WEB-874): значение на экране — лишь дисплейный дефолт, а не бюджет,
   * который реально уйдёт в запрос, поэтому простой режим остаётся на
   * консервативной полке (как до этого фикса) — иначе показанное разошлось
   * бы с тем, что бэкенд реально зарезервирует (WEB-904).
   *
   * WEB-1242 (хвост, 24.08): Pro-ветка БОЛЬШЕ НЕ игнорирует эти флаги. Раньше
   * число картинок считалось от сырых screenCount/paywallScreenCount «как на
   * экране» (QA 06.08 просил Pro не трогать) — но именно там это и оказалось
   * неправдой: нетронутый счётчик в запрос не уходит, и сервер резервирует не
   * «как на экране», а верхнюю границу. Форма показывала 319 Cr против 709 Cr
   * серверного резерва и по своему числу пускала человека в отказ.
   */
  screenCountTouched?: boolean;
  /** Тот же смысл, для paywallScreenCount (только полная воронка). */
  paywallScreenCountTouched?: boolean;
  /**
   * WEB-828: концовка «редирект» — пейволл-этапа нет, и его число экранов в
   * запрос не уходит ВООБЩЕ, даже если человек его трогал (см. noPaywallStage
   * в GenerateFunnelModal). WEB-1242 (хвост): сервер в этом случае подставит
   * свой дефолт (DEFAULT_PAYWALL_SCREEN_COUNT), поэтому и оценка обязана
   * считать по дефолту, а не по значению, оставшемуся в поле формы.
   */
  noPaywallStage?: boolean;
  /**
   * WEB-1985: переключатель «без ИИ-картинок». 'none' — явный отказ от
   * картинок: сервер пропускает блок картинок целиком и не резервирует за
   * них ни кредита (`cost-catalog-retail.ts::generationImageCeilingCredits` —
   * условие `images !== 'none'` стоит ПОСЛЕ effectivePro, то есть отказ
   * сильнее и режима Pro, и стилевой картинки). Текстовый потолок отказ НЕ
   * меняет: тариф по-прежнему решает effectivePro. Не передано / 'auto' —
   * картинки как раньше.
   */
  images?: GenerationImages;
}

/** WEB-1985: значения поля `images` запроса генерации (зеркало StartGenerationDto). */
export type GenerationImages = 'auto' | 'none';

/**
 * Фактический режим — ровно как его определяет бэкенд на постановке в очередь.
 * Держать в одном месте: от него зависит и тариф, и наличие картинок.
 */
export function isEffectivePro(
  mode: EstimateMode,
  styleImageKey?: string,
): boolean {
  return mode === 'pro' || Boolean(styleImageKey);
}

/** Число из каталога цен, если оно там есть; иначе запасное зеркало. */
function catalogNumber(
  catalog: Record<string, number> | null | undefined,
  key: string,
  fallback: number,
): number {
  const value = catalog?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Счётчики экранов ровно в том виде, в каком они уедут (или не уедут) в запрос. */
export interface RequestScreenCounts {
  screenCount?: number;
  paywallScreenCount?: number;
}

/**
 * WEB-1242 (хвост): что из счётчиков экранов РЕАЛЬНО уедет в запрос.
 *
 * Зеркало `screenCounts` в GenerateFunnelModal.handleSubmit — единственное
 * место, где решается, попадёт число экранов в запрос или нет:
 *  - нетронутый счётчик (WEB-874) не уходит вовсе: на экране стоит лишь
 *    дисплейный дефолт, а не выбор человека;
 *  - при концовке «редирект» (WEB-828) пейволл-этапа нет, и число его
 *    экранов не уходит даже тронутым.
 *
 * Оценка обязана считать ровно от этого, а не от того, что видно в полях:
 * сервер получит именно этот набор и по нему зарезервирует деньги. Форма
 * СТРОИТ запрос этой же функцией (GenerateFunnelModal.handleSubmit) — правило
 * «что уедет» одно на показанную цену и на сам запрос, разойтись им негде.
 */
export function requestScreenCounts(
  params: GenerationEstimateParams,
): RequestScreenCounts {
  const paywallSendable =
    params.composition === 'funnel' &&
    !params.noPaywallStage &&
    params.paywallScreenCountTouched !== false;
  return {
    ...(params.screenCountTouched !== false && {
      screenCount: Math.max(0, params.screenCount || 0),
    }),
    ...(paywallSendable && {
      paywallScreenCount: Math.max(0, params.paywallScreenCount || 0),
    }),
  };
}

/**
 * WEB-1985: уедет ли в запрос отказ от картинок. Одно правило и для запроса
 * (GenerateFunnelModal.handleSubmit строит payload этой функцией), и для
 * сторожа контракта на бэкенде: поле отправляется только из Pro — в простом
 * режиме картинок нет и так, а переключатель там не показывается.
 */
export function requestImages(
  params: Pick<GenerationEstimateParams, 'mode' | 'images'>,
): { images?: 'none' } {
  return params.mode === 'pro' && params.images === 'none'
    ? { images: 'none' }
    : {};
}

/**
 * Сколько экранов сервер возьмёт в потолок КАРТИНОК. Зеркало
 * `cost-catalog-retail.ts::totalScreensForImageEstimate` (WEB-1242), включая
 * его ветку «число экранов не прислано»: там сервер резервирует верхнюю
 * границу СВОЕГО вида генерации (квиз — 40, экраны оплаты — 15, полная
 * воронка — 40 плюс явные или дефолтные 2 экрана оплаты), а не нижний порог
 * в 12 картинок.
 *
 * Числа максимумов берутся из каталога цен (сервер отдаёт их теми же ключами,
 * что и порог капа картинок); константы ниже — запасной вариант на случай
 * старого ответа. Сцепка формул обеих сторон проверяется структурно:
 * backend/src/ai-funnels/cost/generation-estimate-contract.spec.ts зовёт
 * настоящие функции сервера и настоящую функцию отсюда и сравнивает суммы —
 * разойтись молча они больше не могут.
 */
function totalScreensForImageEstimate(
  params: GenerationEstimateParams,
): number {
  const { screenCount, paywallScreenCount } = requestScreenCounts(params);
  const quizMax = catalogNumber(
    params.catalog,
    QUIZ_SCREEN_COUNT_MAX_KEY,
    DEFAULT_QUIZ_SCREEN_COUNT_MAX,
  );
  const paywallMax = catalogNumber(
    params.catalog,
    PAYWALL_SCREEN_COUNT_MAX_KEY,
    DEFAULT_PAYWALL_SCREEN_COUNT_MAX,
  );

  // WEB-2374 п.10 (пачка 48): при НЕ заданном числе экранов оплаты тут стояло
  // умолчание 2 — «как у настоящей генерации». На деле генерация в этом случае
  // читает число из текста брифа и берёт до максимума экранов оплаты, поэтому
  // форма показывала цену за два экрана, а списывали за пятнадцать. Показываем
  // и резервируем верхнюю границу — ровно то же число, что теперь берёт сервер
  // (сцепку держит generation-estimate-contract.spec.ts). Явно выбранное
  // человеком число по-прежнему уважается.
  if (screenCount === undefined) {
    if (params.composition === 'quiz') return quizMax;
    if (params.composition === 'paywall') return paywallMax;
    return quizMax + (paywallScreenCount ?? paywallMax);
  }
  if (params.composition !== 'funnel') return screenCount;
  return screenCount + (paywallScreenCount ?? paywallMax);
}

/**
 * Сколько экранов сервер возьмёт в потолок ТЕКСТОВОЙ модели. Зеркало
 * `funnel-generation.service.ts::totalScreensForEstimate` (WEB-830/WEB-1371):
 * там «число экранов не прислано» остаётся «не задано» — цена текста от вида
 * генерации не зависит, и потолком становится каталожная полка.
 */
function totalScreensForTextEstimate(
  params: GenerationEstimateParams,
): number | undefined {
  const { screenCount, paywallScreenCount } = requestScreenCounts(params);
  if (screenCount === undefined) return undefined;
  if (params.composition !== 'funnel') return screenCount;
  return screenCount + (paywallScreenCount ?? DEFAULT_PAYWALL_SCREEN_COUNT);
}

/**
 * Верхняя граница числа картинок. Зеркало
 * `image-generation.service.ts`: Math.max(порог, число экранов).
 * В простом режиме картинок нет вовсе.
 */
export function estimateImageCount(params: GenerationEstimateParams): number {
  if (!isEffectivePro(params.mode, params.styleImageKey)) return 0;
  // WEB-1985: явный отказ гасит картинки при ЛЮБОМ основании для Pro —
  // и при режиме, и при стилевой картинке (зеркало generationImageCeilingCredits).
  if (params.images === 'none') return 0;
  const floor = catalogNumber(
    params.catalog,
    IMAGE_CAP_FLOOR_KEY,
    DEFAULT_IMAGE_CAP_FLOOR,
  );
  return Math.max(floor, totalScreensForImageEstimate(params));
}

/**
 * simpleGenerationCeiling — WEB-830 QA-hold fix (06.08). Зеркало backend
 * cost-catalog-retail.ts::estimateSimpleGenerationCeilingCredits — та же
 * пропорция, тот же денежный смысл (потолок). Держать формулу СИНХРОННО с
 * бэком — она проверена контрактным тестом на стороне бэка
 * (generation-estimate-contract.spec.ts).
 *
 * touched-семантика (WEB-874) читается ЗДЕСЬ, а не через totalScreens() —
 * та функция служит и Pro-ветке (число картинок), которая сознательно НЕ
 * гейтится по touched (QA 06.08: механизм Pro не трогать).
 */
function simpleGenerationCeiling(
  flatCeiling: number,
  params: GenerationEstimateParams,
): number {
  // WEB-1371 (догон, зеркало серверной правки): экраны оплаты в полной
  // воронке генерируются ВСЕГДА (по умолчанию 2), даже если поле не трогали —
  // сервер считает их в потолке, значит и показанная здесь оценка обязана,
  // иначе она НИЖЕ того, что реально зарезервируется. WEB-1242 (хвост): весь
  // разбор «что уедет в запрос» вынесен в totalScreensForTextEstimate —
  // одно правило на текстовую и картиночную части, а не две копии.
  const safeTotal = totalScreensForTextEstimate(params);
  if (safeTotal === undefined) return flatCeiling;

  // WEB-2389: эталон — замороженное число, а не сумма максимумов каталога.
  const scaled = Math.ceil(
    (flatCeiling * safeTotal) / SIMPLE_GENERATION_SCREEN_REFERENCE,
  );
  // WEB-1371 (догон): пол потолка — себестоимость самой генерации (зеркало
  // costFor(SIMPLE_GENERATION)=40 на сервере): для 1-экранного заказа сервер
  // больше не резервирует 2 кредита, и показывать 2 здесь было бы враньём.
  return Math.min(
    flatCeiling,
    Math.max(SIMPLE_GENERATION_COST_FLOOR_CREDITS, scaled),
  );
}

/**
 * Оценка-потолок в кредитах. `null` — каталог ещё не загружен, показывать
 * нечего (число выдумывать нельзя: это денежный путь).
 */
export function estimateGenerationCredits(
  params: GenerationEstimateParams,
): number | null {
  const catalog = params.catalog;
  if (!catalog) return null;

  const effectivePro = isEffectivePro(params.mode, params.styleImageKey);
  const llmKey = effectivePro ? 'pro_generation' : 'simple_generation';
  const flatLlmCeiling = catalog[llmKey];
  if (typeof flatLlmCeiling !== 'number' || !Number.isFinite(flatLlmCeiling)) {
    return null;
  }
  // WEB-830 QA-hold fix (06.08): PRO ceiling stays flat (its growth already
  // comes from the image term below, QA-verified 05.08) — only SIMPLE scales.
  const llmCeiling = effectivePro
    ? flatLlmCeiling
    : simpleGenerationCeiling(flatLlmCeiling, params);

  // Нет цены картинки в каталоге — картинки в оценку не выдумываем (иначе
  // NaN на денежном пути), но и текстовую часть не теряем.
  const imagePrice = catalog['image'];
  if (typeof imagePrice !== 'number' || !Number.isFinite(imagePrice)) {
    return llmCeiling;
  }

  return llmCeiling + imagePrice * estimateImageCount(params);
}

/**
 * WEB-1985: сколько кредитов оценки снимает отказ от картинок — разница
 * оценки «с картинками» и «без», посчитанная ТОЙ ЖЕ функцией, что и ценник
 * у кнопки. Подпись переключателя обещает ровно это число, поэтому отдельной
 * формулы здесь быть не должно: разойтись с ценником ей было бы негде, кроме
 * как в копии. `null` — каталог не загружен, обещать нечего.
 */
export function estimateImagesSavingCredits(
  params: GenerationEstimateParams,
): number | null {
  const withImages = estimateGenerationCredits({ ...params, images: 'auto' });
  const withoutImages = estimateGenerationCredits({
    ...params,
    images: 'none',
  });
  if (withImages === null || withoutImages === null) return null;
  return withImages - withoutImages;
}
